import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LifeBuoy, Plus } from "lucide-react";
import { PageHeader } from "@/components/control/PageHeader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api } from "@/lib/api";
import {
  Section, Loadable, Empty, Status, Tabbar, TabPanel, useTabs, RowLink, ActionButton, SelectField, useLoad, fmt,
} from "@/components/control/kit";

const AREAS = ["Konto und Anmeldung", "Aktivität", "Kreis oder Gruppe", "Access Pass", "Barrierefreiheit", "Sonstiges"].map((x) => ({ value: x, label: x }));
const URGENCY = [{ value: "niedrig", label: "Niedrig" }, { value: "normal", label: "Normal" }, { value: "hoch", label: "Hoch" }, { value: "dringend", label: "Dringend" }];
const STATUS = [{ value: "all", label: "Alle Status" }, { value: "neu", label: "Neu" }, { value: "in_bearbeitung", label: "In Bearbeitung" },
  { value: "wartet_auf_mitglied", label: "Wartet auf Mitglied" }, { value: "gelöst", label: "Gelöst" }, { value: "entwurf", label: "Entwurf" }];

function CreateTicket() {
  const navigate = useNavigate();
  const members = useLoad("/members");
  const options = (members.data?.members || []).map((m) => ({ value: m.id, label: `${m.name} (${m.city || "–"})` }));
  return (
    <ActionButton label="Supportanfrage erfassen" icon={Plus} variant="default" testId="create-ticket" submitLabel="Supportanfrage erfassen"
      page="neu" pageSubtitle="Erfasse nur den für die Unterstützung notwendigen Kontext."
      description="Erfasse nur die für die Lösung nötigen Angaben. Keine Gesundheitsdaten oder Passwörter eintragen."
      fields={[
        { name: "subject", label: "Thema", required: true },
        { name: "area", label: "Bereich", type: "select", options: AREAS, required: true },
        { name: "member_id", label: "Betroffenes Mitglied (optional)", type: "select", options, placeholder: "Kein Mitglied zugeordnet" },
        { name: "message", label: "Anliegen", type: "textarea", required: true },
        { name: "attachment_note", label: "Hinweis zu Anhängen", help: "Dateien werden nicht hochgeladen – nur Beschreibung." },
        { name: "urgency", label: "Dringlichkeit", type: "select", options: URGENCY, default: "normal", placeholder: false },
        { name: "draft", label: "Als Entwurf speichern", type: "checkbox" },
      ]}
      onSubmit={async (v) => {
        const { data } = await api.post("/support/tickets", { ...v, member_id: v.member_id || null });
        navigate(`/support/${data.ticket.id}`, { replace: true });
        return `Supportfall ${data.ticket.number} erfasst.`;
      }} />
  );
}

export default function Support() {
  const [scope, setScope] = useState("mine");
  const [status, setStatus] = useState("all");
  const st = useLoad(`/support/tickets?${new URLSearchParams({ scope, status })}`);
  const tabs = useTabs([{ value: "mine", label: "Mir zugewiesen" }, { value: "all", label: "Alle sichtbaren Fälle" }], scope);
  return (
    <div>
      <PageHeader title="Support" subtitle="Anfragen erfassen, übernehmen, beantworten und lösen." icon={LifeBuoy} actions={<CreateTicket />} />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <Tabbar tabs={tabs} onChange={setScope} testId="support-scope" />
        <SelectField id="support-status" label="Status" value={status} onChange={setStatus} options={STATUS} testId="support-status" className="mb-4 w-56" />
      </div>
      <TabPanel tabs={tabs} testId="support-scope-panel">
      <Section title="Supportfälle" testId="support-section">
        <Loadable state={st}>
          {(d) => (
            <div className="overflow-x-auto">
              <Table data-testid="tickets-table">
                <TableHeader><TableRow><TableHead>Fall</TableHead><TableHead>Bereich</TableHead><TableHead>Mitglied</TableHead>
                  <TableHead>Dringlichkeit</TableHead><TableHead>Zuständig</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {d.tickets.length === 0 && <TableRow><TableCell colSpan={6}><Empty text="Keine Supportfälle in dieser Ansicht." /></TableCell></TableRow>}
                  {d.tickets.map((t) => (
                    <TableRow key={t.id} data-testid={`ticket-row-${t.id}`}>
                      <TableCell><RowLink to={`/support/${t.id}`} testId={`ticket-link-${t.id}`}>{t.number} · {t.subject}</RowLink>
                        <p className="text-xs text-muted-foreground">{fmt(t.created_at)}</p></TableCell>
                      <TableCell className="text-sm">{t.area}</TableCell>
                      <TableCell className="text-sm">{t.member_name || "–"}</TableCell>
                      <TableCell><Status value={t.urgency} /></TableCell>
                      <TableCell className="text-sm">{t.assigned_to?.name || "–"}</TableCell>
                      <TableCell><Status value={t.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Loadable>
      </Section>
      </TabPanel>
    </div>
  );
}
